import General from "../common/General/General";
import { Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Service, Subservice } from "../common/types";
import { formatServiceData } from "../Show/common/helpers";
import {
  useV2CreateServiceMutation,
  useV2CreateSubserviceMutation,
  useV2WhoAmIQuery,
  V2SubserviceResource,
  V2LevelOfEffortResource,
} from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { useListResourcesForAccount } from "../common/api";
import { Option } from "../Show/types";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";

const New = ({ permission, allProjectVariables }): JSX.Element => {
  // The account id is the only thing this page needed off the account, and
  // `/v2/me` already carries it - no second request for one field.
  const { data: user } = useV2WhoAmIQuery();
  const accountId = user?.data?.attributes?.["account-id"];
  const navigate = useNavigate();
  const location = window.location.href;
  const managedService: boolean = location.includes("managed_services");
  const [createService] = useV2CreateServiceMutation();
  const [createSubservice] = useV2CreateSubserviceMutation();
  const { resources } = useListResourcesForAccount();
  const [resourceOptions, setResourceOptions] = useState<Option[]>([]);
  const [saveEnabled, setSaveEnabled] = useState<boolean>(false);
  const [service, setService] = useState<Service>({
    id: "",
    name: "",
    phase: "",
    category: "",
    resource: "",
    sku: "",
    hours: "",
    tags: [],
    teams: [],
    frequency: "",
    languages: {},
    "service-description": "",
    subservices: [],
    guidance: "",
    variables: allProjectVariables,
  });
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleFailResponse = (msg) => {
    setLoading(false);
    setErrorMessage(msg || "Something went wrong.");
    setShowFailAlert(true);
  };

  const onSave = () => {
    setLoading(true);
    if (!accountId) {
      handleFailResponse("");
      return;
    }
    const data = formatServiceData(service, String(accountId), managedService);
    delete data.id;

    createService({ body: { data } })
      .unwrap()
      .then((res) => {
        const id = res?.data?.id;
        if (id) {
          const promises = service.subservices.map((subservice) => {
            let data: V2SubserviceResource = {
              type: "subservices",
              attributes: {
                name: subservice.name,
                "suggested-hours": subservice?.hours
                  ? Number(subservice.hours)
                  : undefined,
                quantity: subservice.quantity,
                position: subservice.position,
              },
              relationships: {
                resource: {
                  data: {
                    type: "resources",
                    id: subservice?.resource
                      ? String(subservice.resource)
                      : String(service.resource),
                  },
                },
                service: {
                  data: {
                    type: "services",
                    id,
                  },
                },
              },
            };
            return createSubservice({ body: { data } });
          });

          Promise.all(promises)
            .then((res) => {
              navigate(
                `/admin/${
                  managedService ? "managed_services" : "tasks"
                }/${id}/edit`,
                {
                  state: { from: location },
                }
              );
            })
            .catch((err) => {
              handleFailResponse("");
            });
        } else {
          handleFailResponse("");
        }
      })
      .catch((error) => handleFailResponse(error?.data?.errors?.[0]?.detail));
  };

  useEffect(() => {
    if (service.name !== "" && service.category !== "") {
      if (
        (managedService && service.frequency !== "") ||
        (!managedService && service.phase !== "" && service.resource !== "")
      ) {
        setSaveEnabled(true);
      } else {
        setSaveEnabled(false);
      }
    } else {
      setSaveEnabled(false);
    }
  }, [service]);

  useEffect(() => {
    if (resources.length !== resourceOptions.length) {
      setResourceOptions(resources);
    }
  }, [resources]);

  return (
    <div className="profServicesV2 legacyPageBleed">
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <div className="headerContainer">
        <div className="actionBtns">
          <Button
            className="cancelButton"
            onClick={(e) =>
              navigate(
                managedService ? "/admin/managed_services" : "/admin/tasks"
              )
            }
          >
            Cancel
          </Button>
          <h2>Add a {managedService ? "Managed" : "Professional"} Service</h2>
        </div>

        <div className={"actionBtns"}>
          <Button
            className={`button ${!saveEnabled ? "disabled" : ""}`}
            onClick={() => {
              setLoading(true);
              onSave();
            }}
            disabled={!saveEnabled || loading}
          >
            Save
          </Button>
        </div>
      </div>
      {loading ? (
        <SmallSpinner />
      ) : (
        <General
          service={service}
          setService={setService}
          isManagedService={managedService}
          permission={permission}
          editing={false}
          resourceOptions={resourceOptions}
          refetch={() => null}
          allProjectVariables={allProjectVariables}
        />
      )}
    </div>
  );
};

export default New;
